import { regularSeason, resultsFor, round1 } from './model.js';
function buildGrid(results, pick) {
    const grid = new Map();
    for (const r of results) {
        let row = grid.get(r.ownerId);
        if (!row)
            grid.set(r.ownerId, (row = new Map()));
        row.set(r.week, pick(r));
    }
    return grid;
}
export function pointsForGrid(data) {
    return buildGrid(data.results, (r) => r.score);
}
export function pointsAgainstGrid(data) {
    return buildGrid(data.results, (r) => r.opponentScore);
}
export function marginGrid(data) {
    return buildGrid(data.results, (r) => r.margin);
}
export function weeklyPointChanges(data) {
    return data.owners.map((o) => {
        const weeks = resultsFor(data, o.id);
        const changes = new Map();
        const deltas = [];
        for (let i = 1; i < weeks.length; i++) {
            const prev = weeks[i - 1];
            const cur = weeks[i];
            const delta = cur.score - prev.score;
            changes.set(cur.week, Math.round(delta));
            deltas.push(Math.abs(delta));
        }
        const first = weeks[0]?.score ?? 0;
        const last = weeks[weeks.length - 1]?.score ?? 0;
        return {
            ownerId: o.id,
            changes,
            naiveTotal: Math.round(last - first),
            swing: deltas.length
                ? round1(deltas.reduce((a, b) => a + b, 0) / deltas.length)
                : 0,
        };
    });
}
export function allPlay(data) {
    const reg = regularSeason(data);
    const byWeek = new Map();
    for (const r of reg) {
        const list = byWeek.get(r.week) ?? [];
        list.push(r);
        byWeek.set(r.week, list);
    }
    const wins = new Map();
    const losses = new Map();
    const ties = new Map();
    for (const week of byWeek.values()) {
        for (const me of week) {
            let w = 0;
            let l = 0;
            let t = 0;
            for (const other of week) {
                if (other.ownerId === me.ownerId)
                    continue;
                if (me.score > other.score)
                    w++;
                else if (me.score < other.score)
                    l++;
                // Identical scores happen — two managers both put up 137.4 in week 3
                // of 2025. Counting that as neither a win nor a loss AND leaving it out
                // of the denominator inflates everyone's win rate by pretending the
                // comparison never happened.
                else
                    t++;
            }
            wins.set(me.ownerId, (wins.get(me.ownerId) ?? 0) + w);
            losses.set(me.ownerId, (losses.get(me.ownerId) ?? 0) + l);
            ties.set(me.ownerId, (ties.get(me.ownerId) ?? 0) + t);
        }
    }
    return data.owners.map((o) => {
        const w = wins.get(o.id) ?? 0;
        const l = losses.get(o.id) ?? 0;
        const t = ties.get(o.id) ?? 0;
        const games = w + l + t;
        const played = reg.filter((r) => r.ownerId === o.id).length;
        const mine = reg.filter((r) => r.ownerId === o.id);
        // Ties are half a win on both sides of the comparison, so expected and
        // actual stay in the same currency and `luck` means what it says.
        const expectedWins = games ? round1(((w + t / 2) / games) * played) : 0;
        const actualWins = round1(mine.filter((r) => r.won).length + mine.filter((r) => r.tied).length / 2);
        return {
            ownerId: o.id,
            allPlayWins: w,
            allPlayLosses: l,
            allPlayTies: t,
            expectedWins,
            actualWins,
            luck: round1(actualWins - expectedWins),
        };
    });
}
/** Weekly high scorer — the $25 award. Ties split, so this returns a list. */
export function weeklyHighScorers(data, week) {
    const inWeek = data.results.filter((r) => r.week === week);
    if (inWeek.length === 0)
        return [];
    const max = Math.max(...inWeek.map((r) => r.score));
    return inWeek.filter((r) => r.score === max).map((r) => r.ownerId);
}
export function weeklyLowScorers(data, week) {
    const inWeek = data.results.filter((r) => r.week === week);
    if (inWeek.length === 0)
        return [];
    const min = Math.min(...inWeek.map((r) => r.score));
    return inWeek.filter((r) => r.score === min).map((r) => r.ownerId);
}
/**
 * Heatbreaker: losses by `threshold` or less. The original sheet counts these
 * over the regular season and pays the manager with the most.
 */
export function heatbreakerCounts(data, threshold = 5) {
    const reg = regularSeason(data);
    return data.owners.map((o) => {
        const weeks = reg
            .filter((r) => r.ownerId === o.id && !r.won && !r.tied && Math.abs(r.margin) <= threshold)
            .map((r) => r.week)
            .sort((a, b) => a - b);
        return { ownerId: o.id, count: weeks.length, weeks };
    });
}
/**
 * Biggest single week anyone posted. This is NOT the Goon award — see
 * `mostPointsFor`. Kept because "biggest week of the year" is a fun stat in
 * its own right.
 */
export function biggestWeek(data) {
    const reg = regularSeason(data);
    if (reg.length === 0)
        return null;
    const best = reg.reduce((a, b) => (b.score > a.score ? b : a));
    return { ownerId: best.ownerId, week: best.week, score: best.score };
}
/**
 * Most total points scored in the regular season — the Goon award.
 *
 * "Season high score" on the spreadsheet means the highest season *total*, not
 * the best single week. Confirmed against the 2025 board: Goon went to the
 * manager with 1919.9 total points rather than the one who posted the biggest
 * single week (206.4).
 *
 * It is regularly a photo finish — 2025 was decided by 3.3 points — so this is
 * exactly the award a late ESPN stat correction could flip.
 */
export function mostPointsFor(data) {
    const reg = regularSeason(data);
    if (reg.length === 0)
        return null;
    const totals = new Map();
    for (const r of reg) {
        totals.set(r.ownerId, (totals.get(r.ownerId) ?? 0) + r.score);
    }
    let best = null;
    for (const [ownerId, total] of totals) {
        const rounded = round1(total);
        if (!best || rounded > best.total)
            best = { ownerId, total: rounded };
    }
    return best;
}
/** Most total points scored against you — the Unlucky award. */
export function mostPointsAgainst(data) {
    const reg = regularSeason(data);
    if (reg.length === 0)
        return null;
    const totals = new Map();
    for (const r of reg) {
        totals.set(r.ownerId, (totals.get(r.ownerId) ?? 0) + r.opponentScore);
    }
    let best = null;
    for (const [ownerId, total] of totals) {
        if (!best || total > best.total)
            best = { ownerId, total: round1(total) };
    }
    return best;
}
//# sourceMappingURL=derived.js.map